import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { configRelayQuery, runConfigRelay, type DeviceConfigRelayPage } from '../api/devices'
import { FormActions, FormRow, HalfField } from '../ui/FormGrid'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Configuration Relay - proactivitee/devices/details/configRelay.php:
 * Connect (țintă, adresă, port, Pass Safe, comandă), Output, Recent runs.
 *
 * -----------------------------------------------------------------------------
 * CE A RAMAS AL PANOULUI SI CE A TRECUT PE GRILA COMUNA
 * -----------------------------------------------------------------------------
 *
 * Panoul asta e pe jumatate consola: caseta neagra de raspuns (`cr-out`) si
 * tabla ultimelor rulari (`cr-hist`) nu sunt formular si raman cum sunt - o
 * citire si un tabel, exact cele doua lucruri pe care `FormGrid` le lasa
 * dinadins afara.
 *
 * Cele cinci campuri din "Connect" SUNT formular, si aveau desenul lor:
 * `.cr-field label` scria eticheta cu 11px, majuscule si gri, adica a treia
 * forma de eticheta din produs, dupa fisa contului si My Account. Alea au
 * trecut pe `FormRow` / `HalfField`, si regula `.cr-field` s-a scos din foaia
 * panoului.
 *
 * Nici nu se vedeau ca formular: masurat in Chromium la 1600px, cu foile
 * adevarate servite pe HTTP si panoul in randul desfasurat, cartonasul
 * "Connect" avea 757px si eticheta lui 719px, dar `#crHost` avea 186px si
 * `#crTarget` 153px - `.form-inline .form-control { width: auto }` din
 * invelisul listei, aceeasi pricina descrisa in `FormGrid`. `od-form-row` din
 * `FormRow` o scoate din joc.
 *
 * Ce PLEACA de aici - tinta, adresa, portul, cheia si linia de comanda - nu s-a
 * atins: `runConfigRelay` primeste exact aceleasi cinci valori, si parola sta
 * mai departe in Pass Safe.
 */

export function DeviceConfigRelay({ id }: ScreenProps) {
  const { data, error } = useQuery(configRelayQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Relay page={data} />
}

function Relay({ page }: { page: DeviceConfigRelayPage }) {
  const query = useQueryClient()
  const [target, setTarget] = useState('0')
  const [host, setHost] = useState('')
  const [port, setPort] = useState('22')
  const [cred, setCred] = useState('0')
  const [command, setCommand] = useState('')
  const [out, setOut] = useState('Ready.')
  const [busy, setBusy] = useState(false)
  const targets = page.targets_by_id

  const credById = useMemo(() => {
    const map: Record<string, (typeof page.credentials)[number]> = {}
    for (const c of page.credentials) map[String(c.id)] = c
    return map
  }, [page.credentials])

  const run = async () => {
    setBusy(true)
    setOut('Queued. Waiting for the agent…')
    try {
      const res = await runConfigRelay(page.device.id, {
        target_id: Number(target),
        credential_id: Number(cred),
        command,
        host,
        port: Number(port) || 22,
      })
      if (!res.ok) {
        setOut(res.error || 'Could not queue the command.')
        setBusy(false)
        return
      }
      setOut('Queued as #' + res.command_id + ' to ' + res.user + '@' + res.host + '.\nThe agent on this machine will open SSH. Refresh in a few seconds for the answer.')
      setBusy(false)
      void query.invalidateQueries({ queryKey: ['device', page.device.id, 'config-relay'] })
    } catch {
      setOut('Could not queue the command.')
      setBusy(false)
    }
  }

  return (
    <>
      <style>{`
        .cr-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        @media (max-width: 980px) { .cr-grid { grid-template-columns: 1fr; } }
        .cr-card { background: #fff; border: 1px solid #e7ecf1; border-radius: 8px; padding: 16px 18px; }
        .cr-card h4 { margin: 0 0 14px; font-size: 15px; color: #2c3e50; }
        .cr-note { font-size: 12px; color: #7f8c8d; margin: 0 0 14px; }
        .cr-out { font-family: Menlo, Consolas, monospace; font-size: 12px; background: #1e272e; color: #d5dfe6; border-radius: 6px; padding: 12px; min-height: 160px; white-space: pre-wrap; word-break: break-word; }
        .cr-hist { width: 100%; border-collapse: collapse; font-size: 13px; }
        .cr-hist th, .cr-hist td { padding: 8px 10px; border-bottom: 1px solid #eef2f5; }
        .cr-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
        .cr-pill.ok { background: #e3f6eb; color: #1b7a42; }
        .cr-pill.error { background: #fde8e8; color: #b33939; }
        .cr-pill.waiting { background: #eef1f3; color: #7f8c8d; }
        .cr-empty { color: #7f8c8d; padding: 12px 0; }
      `}</style>
      <p className="cr-note">
        SSH leaves from <strong>this</strong> machine, inside the LAN. The password stays in Pass Safe: it is handed to
        the agent for this run only, never stored on the command.
      </p>
      <div className="cr-grid">
        <div className="cr-card">
          <h4>Connect</h4>
          <FormRow>
            <HalfField
              label="Device"
              htmlFor="crTarget"
              help={page.targets.length === 0 ? 'No SSH targets with an address yet.' : undefined}
            >
              <select
                id="crTarget"
                className="form-control"
                value={target}
                onChange={(ev) => {
                  const value = ev.target.value
                  setTarget(value)
                  const t = targets[value]
                  if (t && t.ip) setHost(t.ip)
                }}
              >
                <option value="0">Select a device…</option>
                {page.targets.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} — {t.managed ? 'Managed' : 'Unmanaged'}
                    {t.ip !== '' ? ' · ' + t.ip : ''}
                  </option>
                ))}
              </select>
            </HalfField>
            <HalfField label="Address" htmlFor="crHost">
              <input id="crHost" className="form-control" type="text" placeholder="10.0.0.1" autoComplete="off" value={host} onChange={(ev) => setHost(ev.target.value)} />
            </HalfField>
          </FormRow>
          <FormRow>
            <HalfField label="Port" htmlFor="crPort">
              <input id="crPort" className="form-control" type="number" min={1} max={65535} value={port} onChange={(ev) => setPort(ev.target.value)} />
            </HalfField>
            <HalfField label="Pass Safe" htmlFor="crCred">
              <select
                id="crCred"
                className="form-control"
                value={cred}
                onChange={(ev) => {
                  const value = ev.target.value
                  setCred(value)
                  const ip = credById[value]?.device_ip || ''
                  if (ip && host.trim() === '') setHost(ip)
                }}
              >
                <option value="0">Select a credential…</option>
                {page.credentials.map((c) => {
                  const ip = (c.device_ip || '').trim()
                  let where = (c.device_name || '').trim()
                  if (ip !== '' && ip !== where) where = (where !== '' ? where + ' · ' : '') + ip
                  return (
                    <option key={c.id} value={c.id} data-user={c.user} data-ip={ip}>
                      {c.user}
                      {c.description !== '' ? ' — ' + c.description : ''}
                      {where !== '' ? ' (' + where + ')' : ''}
                    </option>
                  )
                })}
              </select>
            </HalfField>
          </FormRow>
          {/*
            Linia de comanda sta singura pe rand, ca "Address" din fisa
            contului. `HalfField` nu are latime intreaga, si nu se inventeaza
            una aici: `FormGrid` e al altcuiva in ceasul asta.
          */}
          <FormRow>
            <HalfField label="Command" htmlFor="crCommand">
              <textarea id="crCommand" className="form-control" rows={4} placeholder="show version" value={command} onChange={(ev) => setCommand(ev.target.value)} />
            </HalfField>
          </FormRow>
          <FormActions>
            <button type="button" className="btn btn-theme" id="crRun" disabled={busy} onClick={() => void run()}>
              <i className="fas fa-terminal"></i> Connect
            </button>
          </FormActions>
        </div>
        <div className="cr-card">
          <h4>Output</h4>
          <div className="cr-out" id="crOut">
            {out}
          </div>
        </div>
      </div>
      <div className="cr-card" style={{ marginTop: 16 }}>
        <h4>Recent runs</h4>
        {page.history.length === 0 ? (
          <div className="cr-empty">Nothing has been sent from this machine yet.</div>
        ) : (
          <table className="cr-hist">
            <thead>
              <tr>
                <th>When</th>
                <th>To</th>
                <th>Command</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {page.history.map((h) => (
                <tr key={h.id}>
                  <td>{h.created}</td>
                  <td>{(h.user ? h.user + '@' : '') + h.host}</td>
                  <td>
                    <code>{h.command.slice(0, 80)}</code>
                  </td>
                  <td>
                    <span className={'cr-pill ' + h.state}>{h.state}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  )
}
